import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, GlobeIcon, NewspaperIcon, LinkedinIcon, Loader2Icon, MapPinIcon, SearchIcon, SendIcon, ShieldCheckIcon, SparklesIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NETWORKING_GUESTS, NetworkingGuest } from '../data/networking-guests';

/**
 * Google Apps Script web-app endpoint that appends each introduction request
 * to the Association's spreadsheet. Replace with the deployed /exec URL.
 * Until then, requests fall back to an email to CONTACT_EMAIL.
 */
const INTRO_SHEET_URL = 'https://script.google.com/macros/s/AKfycbzG_BiMq_VHKVkDH_U1JFkDi9aw84wL5nB-tFlH2Gvs8fZpo5ta1nypS4JZf8XsCYHNhA/exec';
const CONTACT_EMAIL = 'info@honduranamerican.org';
const GOLD = '#c9a227';
const NAVY = '#0b1f3f';
const COPY = {
  EN: {
    back: 'Back to event',
    eyebrow: 'Voices of Impact · New York City',
    title: 'Guest Networking Directory',
    intro: 'Everyone in this directory attended Voices of Impact and opted in to keep the conversation going. Browse the room, then request an introduction — the Association passes your note along, so no one’s inbox is published here.',
    privacy: 'No email addresses are shown. Requests are reviewed by the Association before an introduction is made.',
    searchLabel: 'Search guests',
    searchPlaceholder: 'Search by name, role, or organization',
    all: 'All',
    countOne: 'guest',
    countMany: 'guests',
    empty: 'No guests match your search.',
    clear: 'Clear search',
    lookingFor: 'Open to',
    boardBadge: 'HAA Board',
    linkedin: 'LinkedIn',
    website: 'Website',
    feature: 'Feature',
    requestIntro: 'Request an introduction',
    introTo: 'Request an introduction to',
    formIntro: 'Tell us a little about yourself and why you’d like to connect. We’ll forward your note and let you know when it’s been passed along.',
    yourName: 'Your name',
    yourEmail: 'Your email',
    yourOrg: 'Organization or company',
    yourRole: 'Your role or title',
    yourLink: 'LinkedIn or website',
    yourLinkPlaceholder: 'linkedin.com/in/yourname',
    yourLinkHelp: 'Optional, but it helps the guest know who they are meeting.',
    message: 'Why would you like to connect?',
    messagePlaceholder: 'A sentence or two about what you’d like to discuss or explore together.',
    required: 'Required',
    cancel: 'Cancel',
    send: 'Send request',
    sending: 'Sending…',
    successTitle: 'Request sent',
    successText: 'Thank you. The Association has your request and will pass it along to',
    done: 'Close',
    fallbackNote: 'Your email app will open with the request ready to send.',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al evento',
    eyebrow: 'Voices of Impact · Ciudad de Nueva York',
    title: 'Directorio de Networking',
    intro: 'Todas las personas en este directorio asistieron a Voices of Impact y decidieron seguir la conversación. Explore la sala y solicite una presentación: la Asociación hace llegar su mensaje, por lo que aquí no se publica ningún correo.',
    privacy: 'No se muestran correos electrónicos. La Asociación revisa cada solicitud antes de hacer una presentación.',
    searchLabel: 'Buscar invitados',
    searchPlaceholder: 'Buscar por nombre, cargo u organización',
    all: 'Todos',
    countOne: 'invitado',
    countMany: 'invitados',
    empty: 'Ningún invitado coincide con su búsqueda.',
    clear: 'Limpiar búsqueda',
    lookingFor: 'Abierto a',
    boardBadge: 'Junta HAA',
    linkedin: 'LinkedIn',
    website: 'Sitio web',
    feature: 'Reportaje',
    requestIntro: 'Solicitar una presentación',
    introTo: 'Solicitar una presentación con',
    formIntro: 'Cuéntenos brevemente sobre usted y por qué desea conectar. Haremos llegar su mensaje y le avisaremos cuando se haya enviado.',
    yourName: 'Su nombre',
    yourEmail: 'Su correo electrónico',
    yourOrg: 'Organización o empresa',
    yourRole: 'Su cargo o título',
    yourLink: 'LinkedIn o sitio web',
    yourLinkPlaceholder: 'linkedin.com/in/sunombre',
    yourLinkHelp: 'Opcional, pero ayuda a que el invitado sepa a quién va a conocer.',
    message: '¿Por qué le gustaría conectar?',
    messagePlaceholder: 'Una o dos frases sobre lo que le gustaría conversar o explorar juntos.',
    required: 'Obligatorio',
    cancel: 'Cancelar',
    send: 'Enviar solicitud',
    sending: 'Enviando…',
    successTitle: 'Solicitud enviada',
    successText: 'Gracias. La Asociación recibió su solicitud y la hará llegar a',
    done: 'Cerrar',
    fallbackNote: 'Se abrirá su aplicación de correo con la solicitud lista para enviar.',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
function initialsOf(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}
type FormState = {
  name: string;
  email: string;
  org: string;
  role: string;
  link: string;
  message: string;
};
const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  org: '',
  role: '',
  link: '',
  message: ''
};
function IntroRequestDialog({
  guest,
  onClose,
  c
}: {
  guest: NetworkingGuest;
  onClose: () => void;
  c: (typeof COPY)['EN'];
}) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isValid = form.name.trim() !== '' && /^\S+@\S+\.\S+$/.test(form.email.trim()) && form.message.trim() !== '';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const submit = () => {
    if (!isValid || status !== 'idle') return;
    setStatus('sending');
    const payload = {
      type: 'voices-ny-introduction',
      timestamp: new Date().toISOString(),
      requestedGuestId: guest.id,
      requestedGuestName: guest.name,
      requesterName: form.name.trim(),
      requesterEmail: form.email.trim(),
      requesterOrg: form.org.trim(),
      requesterRole: form.role.trim(),
      requesterLink: form.link.trim(),
      message: form.message.trim()
    };
    if (INTRO_SHEET_URL) {
      // text/plain avoids a CORS preflight; the JSON arrives in
      // e.postData.contents on the Apps Script side.
      fetch(INTRO_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.warn('Introduction request submission error:', err);
      });
    } else {
      const subject = `Introduction request — ${guest.name}`;
      const body = [`Requested guest: ${guest.name}`, `From: ${form.name.trim()}`, `Email: ${form.email.trim()}`, form.org.trim() ? `Organization / company: ${form.org.trim()}` : '', form.role.trim() ? `Role / title: ${form.role.trim()}` : '', form.link.trim() ? `LinkedIn / website: ${form.link.trim()}` : '', '', form.message.trim()].filter(Boolean).join('\n');
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    window.setTimeout(() => setStatus('sent'), 600);
  };
  const field = 'w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10';
  const label = 'block text-[11px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2';
  return <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true" aria-label={`${c.introTo} ${guest.name}`} data-id="element-4089" data-mp="w2y6xvfmbcpc">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-4090" data-mp="29zch1k7mlxjr" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-lg my-auto rounded-2xl border border-[#c9a227]/30 bg-[#0b1f3f] shadow-2xl" data-id="element-4091" data-mp="214pi96wgwjgh">
        <button onClick={onClose} aria-label={c.cancel} className="absolute right-4 top-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 transition-colors hover:bg-white/10 hover:text-white" data-id="element-4092" data-mp="1i97ouzdmk67z">
          <XIcon className="w-5 h-5" data-id="element-4093" data-mp="ym4ivjxf6r5o" />
        </button>

        {status === 'sent' ? <div className="px-8 py-14 text-center" data-id="element-4094" data-mp="yohakoszcfvx">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#c9a227]/15 flex items-center justify-center" data-id="element-4095" data-mp="1cmyppy4tkk28">
              <CheckCircle2Icon className="w-7 h-7" style={{
            color: GOLD
          }} data-id="element-4096" data-mp="17l9gmo15qggs" />
            </div>
            <h2 className="font-bebas text-3xl tracking-wider text-white mb-3" data-id="element-4097" data-mp="1miimemr7jms1">
              {c.successTitle}
            </h2>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm mx-auto" data-id="element-4098" data-mp="1hiw6g5jzvsfj">
              {c.successText}{' '}
              <span className="font-semibold text-white" data-id="element-4099" data-mp="1rasbk0ki0it9">{guest.name}</span>.
            </p>
            <button onClick={onClose} className="mt-8 inline-flex items-center justify-center px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.02]" style={{
          backgroundColor: GOLD
        }} data-id="element-4100" data-mp="25akvnd1is0s9">
              {c.done}
            </button>
          </div> : <div className="px-6 sm:px-8 pt-8 pb-7" data-id="element-4101" data-mp="3mi5u6k2b401">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-2" style={{
          color: GOLD
        }} data-id="element-4102" data-mp="3vr2hsc10nzy0">
              {c.introTo}
            </p>
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider text-white leading-none mb-1" data-id="element-4103" data-mp="rd408v158prk">
              {guest.name}
            </h2>
            <p className="text-sm text-white/55 mb-5" data-id="element-4104" data-mp="ncacc1npbayo">
              {guest.title ?? guest.organization ?? guest.sector}
            </p>
            <p className="text-sm leading-relaxed text-white/70 mb-7" data-id="element-4105" data-mp="ddl7b9gwlkzu">
              {c.formIntro}
            </p>

            <div className="space-y-4" data-id="element-4106" data-mp="332n35qlsrjn7">
              <div data-id="element-4107" data-mp="334hrzg06vvwk">
                <label className={label} htmlFor="intro-name" data-id="element-4108" data-mp="330tw4a79uxeo">
                  {c.yourName} <span style={{
                color: GOLD
              }} data-id="element-4109" data-mp="2y28wabvxmfqr">*</span>
                </label>
                <input id="intro-name" type="text" value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} className={field} autoComplete="name" data-id="element-4110" data-mp="2o0y7h3an6alb" />
              </div>
              <div data-id="element-4111" data-mp="32pkw5vm5sep7">
                <label className={label} htmlFor="intro-email" data-id="element-4112" data-mp="32q454mttzsr3">
                  {c.yourEmail} <span style={{
                color: GOLD
              }} data-id="element-4113" data-mp="36plazhd66rws">*</span>
                </label>
                <input id="intro-email" type="email" value={form.email} onChange={e => setForm({
              ...form,
              email: e.target.value
            })} className={field} autoComplete="email" data-id="element-4114" data-mp="3boogt40z525t" />
              </div>
              <div data-id="element-4115" data-mp="1mbupch3nncbv">
                <label className={label} htmlFor="intro-org" data-id="element-4116" data-mp="1mce1fh6mj6hz">
                  {c.yourOrg}
                </label>
                <input id="intro-org" type="text" value={form.org} onChange={e => setForm({
              ...form,
              org: e.target.value
            })} className={field} autoComplete="organization" data-id="element-4117" data-mp="1rbh3qtv7yym9" />
              </div>
              <div data-id="element-4118" data-mp="1o8axtk7lmjib">
                <label className={label} htmlFor="intro-role" data-id="element-4119" data-mp="1o7rqexfkrdij">
                  {c.yourRole}
                </label>
                <input id="intro-role" type="text" value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className={field} autoComplete="organization-title" data-id="element-4120" data-mp="226hooge4rj8h" />
              </div>
              <div data-id="element-4121" data-mp="270lxl56yi1c7">
                <label className={label} htmlFor="intro-link" data-id="element-4122" data-mp="2kya7xj3edq30">
                  {c.yourLink}
                </label>
                <input id="intro-link" type="url" inputMode="url" value={form.link} onChange={e => setForm({
              ...form,
              link: e.target.value
            })} placeholder={c.yourLinkPlaceholder} aria-describedby="intro-link-help" className={field} autoComplete="url" data-id="element-4123" data-mp="2gx8g3nbt8gb2" />
                <p id="intro-link-help" className="mt-2 text-xs leading-relaxed text-white/40" data-id="element-4124" data-mp="2vi2vue6j17ey">
                  {c.yourLinkHelp}
                </p>
              </div>
              <div data-id="element-4125" data-mp="2lnaur0dqk2ff">
                <label className={label} htmlFor="intro-message" data-id="element-4126" data-mp="27ttdjm8iv1sk">
                  {c.message} <span style={{
                color: GOLD
              }} data-id="element-4127" data-mp="27rqde9dnnsa4">*</span>
                </label>
                <textarea id="intro-message" rows={4} value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} placeholder={c.messagePlaceholder} className={`${field} resize-none`} data-id="element-4128" data-mp="2bvy1q5g7b67t" />
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed text-white/45" data-id="element-4129" data-mp="317ci4di7rt97">
              <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-px" style={{
            color: GOLD
          }} data-id="element-4130" data-mp="2wdzyyiwb0enu" />
              <span data-id="element-4131" data-mp="2mi6b43d4wyqr">{INTRO_SHEET_URL ? c.privacy : c.fallbackNote}</span>
            </div>

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3" data-id="element-4132" data-mp="2hitq7o5tq1qt">
              <button onClick={onClose} className="px-6 py-3 rounded-full text-sm font-bold text-white/70 border border-white/20 transition-colors hover:bg-white/5 hover:text-white" data-id="element-4133" data-mp="2dgyr8qbd80g7">
                {c.cancel}
              </button>
              <button onClick={submit} disabled={!isValid || status === 'sending'} className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 enabled:hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed" style={{
            backgroundColor: GOLD
          }} data-id="element-4134" data-mp="bniwpic5q1fy">
                {status === 'sending' ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4135" data-mp="sy4mn710h3vq" />
                    {c.sending}
                  </> : <>
                    <SendIcon className="w-4 h-4" data-id="element-4136" data-mp="bbjyj429uj3" />
                    {c.send}
                  </>}
              </button>
            </div>
          </div>}
      </motion.div>
    </div>;
}
const GuestCard = React.memo(function GuestCard({
  guest,
  c,
  priority,
  onRequest
}: {
  guest: NetworkingGuest;
  c: (typeof COPY)['EN'];
  /** Cards in the first visible row load their portrait immediately. */
  priority: boolean;
  onRequest: (guest: NetworkingGuest) => void;
}) {
  return <article className="group flex flex-col h-full rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden transition-colors duration-200 hover:border-[#c9a227]/40 hover:bg-white/[0.05]" data-id="element-4137" data-mp="3hpeyt9f6d95l">
      {/* Portrait fills the top of the card so faces read at a glance */}
      <div className="relative w-full aspect-square bg-white/[0.06] overflow-hidden" data-id="element-4138" data-mp="3ls39znrd2s5q">
        {guest.photo ? <img src={guest.photo} alt={guest.name} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'low'} decoding="async" width={640} height={640} className="absolute inset-0 w-full h-full object-cover object-[center_22%]" data-id="element-4139" data-mp="2y3sez18cjmpd" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-4140" data-mp="2fbznjb8xe9jr">
            <span className="font-bebas text-5xl tracking-wider text-white/25" data-id="element-4141" data-mp="2f9wndye2701b">
              {initialsOf(guest.name)}
            </span>
          </div>}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5" data-id="element-4142" data-mp="ptkljlz1vcoo">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f]" style={{
          backgroundColor: GOLD
        }} data-id="element-4143" data-mp="um3ycgpwy3sf">
            {guest.sector}
          </span>
          {guest.secondarySector && <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f]" style={{
          backgroundColor: GOLD
        }} data-id="element-4144" data-mp="19j4q2k83tyqw">
              {guest.secondarySector}
            </span>}
        </div>
        {guest.boardOrder !== undefined && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white bg-[#0b1f3f]/85 ring-1 ring-white/20" data-id="element-4145" data-mp="14j8o0qiywhdz">
            {c.boardBadge}
          </span>}
      </div>

      <div className="p-6 flex-1 flex flex-col" data-id="element-4146" data-mp="3pbk7aub9m2ko">
        <h3 className="font-bebas text-3xl tracking-wider text-white leading-none" data-id="element-4147" data-mp="ksz6xsaxmit2">
          {guest.name}
        </h3>
        {guest.title && <p className="mt-2 text-sm font-semibold text-white/80 leading-snug" data-id="element-4148" data-mp="fs1p5gtu3qs3">
            {guest.title}
          </p>}
        {guest.organization && <p className="text-sm text-white/50 leading-snug" data-id="element-4149" data-mp="5vyran60meay">
            {guest.organization}
          </p>}
        {guest.location && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/45" data-id="element-4150" data-mp="13l0b9a6h719v">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-4151" data-mp="ykmeioabrdr5" />
            {guest.location}
          </p>}

        {guest.bio && <p className="mt-4 text-sm leading-relaxed text-white/65" data-id="element-4152" data-mp="ou19ai05baz9">
            {guest.bio}
          </p>}

        {guest.lookingFor && <div className="mt-5 rounded-xl border border-[#c9a227]/20 bg-[#c9a227]/[0.07] px-4 py-3" data-id="element-4153" data-mp="1ma6ynai7sh20">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{
          color: GOLD
        }} data-id="element-4154" data-mp="1hhx41h65mste">
              {c.lookingFor}
            </p>
            <p className="text-sm leading-relaxed text-white/75" data-id="element-4155" data-mp="1ttpo9zlyr8jk">
              {guest.lookingFor}
            </p>
          </div>}

        <div className="mt-auto pt-6" data-id="element-4156" data-mp="1yudqzijk1dtm">
          <div className="flex flex-wrap items-center gap-2" data-id="element-4157" data-mp="23rdvhp0ra8im">
            {guest.linkedin && <a href={guest.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4158" data-mp="28r0ao4059yu2">
                <LinkedinIcon className="w-3.5 h-3.5" data-id="element-4159" data-mp="2mgu1usz4gv6y" />
                {c.linkedin}
              </a>}
            {guest.website && <a href={guest.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4160" data-mp="2wf9nwhjale9l">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-4161" data-mp="8pvzdepdee37" />
                {c.website}
              </a>}
            {guest.feature && <a href={guest.feature} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white" data-id="element-4162" data-mp="hs5d0dfxqnkt">
                <NewspaperIcon className="w-3.5 h-3.5" data-id="element-4163" data-mp="wg8htji8rt81" />
                {c.feature}
              </a>}
          </div>
          {guest.acceptsIntros !== false && <button onClick={() => onRequest(guest)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.01]" style={{
          backgroundColor: GOLD
        }} data-id="element-4164" data-mp="15ihqe3ty9cis">
              <SendIcon className="w-4 h-4" data-id="element-4165" data-mp="2gzc84e6lyase" />
              {c.requestIntro}
            </button>}
        </div>
      </div>
    </article>;
});
export function VoicesOfImpactNetworkingPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState<string>('');
  const [activeGuest, setActiveGuest] = useState<NetworkingGuest | null>(null);

  // The input stays instant while filtering settles, so typing never waits on
  // a re-render of the full directory.
  const [deferredQuery, setDeferredQuery] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const id = window.setTimeout(() => setDeferredQuery(query), 120);
    return () => window.clearTimeout(id);
  }, [query]);
  const openRequest = useCallback((guest: NetworkingGuest) => {
    setActiveGuest(guest);
  }, []);
  const sectors = useMemo(() => Array.from(new Set(NETWORKING_GUESTS.flatMap(g => [g.sector, g.secondarySector].filter(Boolean)) as string[])).sort((a, b) => a.localeCompare(b)), []);
  const guests = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    const matches = NETWORKING_GUESTS.filter(g => {
      if (sector && g.sector !== sector && g.secondarySector !== sector) return false;
      if (!q) return true;
      return [g.name, g.title, g.organization, g.location, g.bio].filter(Boolean).some(field => (field as string).toLowerCase().includes(q));
    });

    // HAA board members lead the directory, then the evening's featured guests.
    const pinned = matches.filter(g => g.boardOrder !== undefined || g.featuredOrder !== undefined).sort((a, b) => (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity) || (a.featuredOrder ?? Infinity) - (b.featuredOrder ?? Infinity));

    // Everyone else is interleaved across industries so the grid reads as a
    // mix of fields rather than one long block per sector. Guests without a
    // portrait trail the photographed ones so the grid stays visually solid.
    const unpinned = matches.filter(g => g.boardOrder === undefined && g.featuredOrder === undefined);
    const rest = unpinned.filter(g => Boolean(g.photo));
    const withoutPhoto = unpinned.filter(g => !g.photo).sort((a, b) => a.name.localeCompare(b.name));
    const bySector = new Map<string, NetworkingGuest[]>();
    rest.slice().sort((a, b) => a.name.localeCompare(b.name)).forEach(g => {
      const bucket = bySector.get(g.sector);
      if (bucket) bucket.push(g);else bySector.set(g.sector, [g]);
    });
    const buckets = Array.from(bySector.keys()).sort((a, b) => a.localeCompare(b)).map(key => bySector.get(key) as NetworkingGuest[]);
    const interleaved: NetworkingGuest[] = [];
    for (let row = 0; interleaved.length < rest.length; row++) {
      buckets.forEach(bucket => {
        if (bucket[row]) interleaved.push(bucket[row]);
      });
    }
    return [...pinned, ...interleaved, ...withoutPhoto];
  }, [deferredQuery, sector]);
  const chip = (active: boolean) => `px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.12em] border transition-colors duration-150 ${active ? 'text-[#0b1f3f] border-transparent' : 'text-white/60 border-white/15 hover:border-white/35 hover:text-white'}`;
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4166" data-mp="uu9gik0hi4y5">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4167" data-mp="ztudiow3g388">
        <a href="#/voices-of-impact-ny" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-white" data-id="element-4168" data-mp="nctek8af0ui5">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4169" data-mp="ift6jru09hoe" />
          {c.back}
        </a>
        <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] border transition-colors duration-150 hover:bg-[#c9a227] hover:text-[#0b1f3f]" style={{
        borderColor: `${GOLD}66`,
        color: GOLD
      }} data-id="element-4170" data-mp="eblf3mw5xnmh">
          {isEs ? 'English' : 'Español'}
        </button>
      </header>

      <main data-id="element-4171" data-mp="3quur2uqtuten">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-10" data-id="element-4172" data-mp="3qv4kksb2zip3">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
          color: GOLD
        }} data-id="element-4173" data-mp="3mqn0st4q15eb">
            {c.eyebrow}
          </p>
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-none mb-6" data-id="element-4174" data-mp="384pyu7nmn00u">
            {c.title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-white/70 max-w-3xl" data-id="element-4175" data-mp="3c8e4ulwm1njz">
            {c.intro}
          </p>
          <div className="mt-6 inline-flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 max-w-2xl" data-id="element-4176" data-mp="2y9eijkwklkqz">
            <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
            color: GOLD
          }} data-id="element-4177" data-mp="3qv7gnh6x8b7f" />
            <p className="text-sm leading-relaxed text-white/60" data-id="element-4178" data-mp="3vuam32qtcjfx">{c.privacy}</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-6" data-id="element-4179" data-mp="3h3tjhg2fznhg">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-4180" data-mp="3m3pmbbzel8vf">
            <div className="relative flex-1 max-w-md" data-id="element-4181" data-mp="doxh92q02lwm">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" data-id="element-4182" data-mp="dne2xhnwmkxh" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} aria-label={c.searchLabel} placeholder={c.searchPlaceholder} className="w-full rounded-full bg-white/5 border border-white/15 pl-11 pr-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10" data-id="element-4183" data-mp="ihqqa2si4gei" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4184" data-mp="1k7qld7o04d25">
              <button onClick={() => setSector('')} className={chip(sector === '')} style={sector === '' ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4185" data-mp="1f8x2t0tuxvv4">
                {c.all}
              </button>
              {sectors.map(s => <button key={s} onClick={() => setSector(s)} className={chip(sector === s)} style={sector === s ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4186" data-mp="34msi6stak2o7">
                  {s}
                </button>)}
            </div>
          </div>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-white/40" data-id="element-4187" data-mp="3opy4mit7vhsy">
            {guests.length} {guests.length === 1 ? c.countOne : c.countMany}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-20" data-id="element-4188" data-mp="1dx5v971fez9">
          {guests.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-16 text-center" data-id="element-4189" data-mp="6b751gk8b50a">
              <SparklesIcon className="w-8 h-8 mx-auto mb-4" style={{
            color: `${GOLD}80`
          }} data-id="element-4190" data-mp="bcmpb6el17hm" />
              <p className="text-base text-white/65 mb-5" data-id="element-4191" data-mp="kbq78720jguj">{c.empty}</p>
              <button onClick={() => {
            setQuery('');
            setSector('');
          }} className="px-6 py-2.5 rounded-full text-sm font-bold text-white/75 border border-white/20 transition-colors hover:border-white/40 hover:text-white" data-id="element-4192" data-mp="k8vnkhqtq026">
                {c.clear}
              </button>
            </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch" data-id="element-4193" data-mp="29qir8ofpwl3a">
              {guests.map((guest, i) => <GuestCard key={guest.id} guest={guest} c={c} priority={i < 4} onRequest={openRequest} data-id="element-4194" data-mp="20soz7pozkdlx" data-mpi={i} />)}
            </div>}
        </section>
      </main>

      <footer className="border-t border-white/10 px-6 py-8 text-center" data-id="element-4195" data-mp="2ogqa359n064i">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40" data-id="element-4196" data-mp="2ji5b1964fshn">
          {c.footer}
        </p>
      </footer>

      <AnimatePresence data-id="element-4197" data-mp="1d2manjka5ps2">
        {activeGuest && <IntroRequestDialog guest={activeGuest} c={c} onClose={() => setActiveGuest(null)} data-id="element-4198" data-mp="1d001jfhqr27q" />}
      </AnimatePresence>
    </div>;
}